import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxPermissionsService } from 'ngx-permissions';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialControllerService,
  Gateway,
  PaymentResponseMessage,
  PaymentResponseMessageType
} from 'src/app/admin-api';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-payment-response-message-form',
  templateUrl: './payment-response-message-form.component.html',
  styleUrl: './payment-response-message-form.component.scss'
})
export class PaymentResponseMessageFormComponent implements OnInit {
  @Input()
  paymentResponseMessage: PaymentResponseMessage | undefined;

  @Output()
  buttonAction = new EventEmitter<PaymentResponseMessage | null>();

  @Input()
  permission = false;

  form = this.formGroup;
  paymentResponseTypes: Array<PaymentResponseMessageType>;
  gateways: Array<Gateway>;

  constructor(
    private financialService: FinancialControllerService,
    private messageService: MessageService,
    private permissionsService: NgxPermissionsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue
    ]);
    try {
      await Promise.all([this.getResponseTypes(), this.getGateways()]);
      this.form = this.formGroup;
      if (this.paymentResponseMessage?.paymentResponseMessageId) {
        this.form.controls['paymentResponseMessageId'].enable();
      }
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  async submit(): Promise<void> {
    if (this.form?.valid) {
      LoaderService.showLoader();
      try {
        if (this.paymentResponseMessage?.paymentResponseMessageId) {
          this.paymentResponseMessage = await lastValueFrom(
            this.financialService
              .updateResponseMessage(this.form.getRawValue())
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            severity: 'success',
            detail: 'Regra atualizada com sucesso.',
            summary: 'Sucesso'
          });
        } else {
          this.paymentResponseMessage = await lastValueFrom(
            this.financialService
              .createResponseMessage(this.form.getRawValue())
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            severity: 'success',
            detail: 'Código de resposta cadastrado com sucesso.',
            summary: 'Sucesso'
          });
        }
        this.buttonAction.emit(this.paymentResponseMessage);
        LoaderService.showLoader(false);
      } catch (error: any) {
        LoaderService.showLoader(false);
        AppDialogService.showErrorDialog(error);
      }
    }
  }

  async getResponseTypes(): Promise<void> {
    this.paymentResponseTypes = await lastValueFrom(
      this.financialService
        .findAllPaymentResponseMessageTypes()
        .pipe(map((data) => data.result))
    );
  }

  async getGateways(): Promise<void> {
    this.gateways = await lastValueFrom(
      this.financialService.findGatewaysList().pipe(map((data) => data.result))
    );
  }

  get formGroup() {
    return new FormGroup({
      paymentResponseMessageId: new FormControl<number | undefined>({
        disabled:
          !this.permission ||
          !this.paymentResponseMessage?.paymentResponseMessageId,
        value: this.paymentResponseMessage?.paymentResponseMessageId
      }),
      code: new FormControl<string | undefined>(
        {
          value: this.paymentResponseMessage?.code,
          disabled:
            !this.permission ||
            !!this.paymentResponseMessage?.paymentResponseMessageId
        },
        [Validators.required, Validators.minLength(1), Validators.maxLength(10)]
      ),
      paymentResponseMessageTypeId: new FormControl<number | undefined>(
        {
          value: this.paymentResponseMessage?.paymentResponseMessageTypeId,
          disabled: !this.permission || !this.permission
        },
        Validators.required
      ),
      gatewayId: new FormControl<number | undefined>(
        {
          value: this.paymentResponseMessage?.gatewayId,
          disabled:
            !this.permission ||
            !!this.paymentResponseMessage?.paymentResponseMessageId
        },
        Validators.required
      )
    });
  }

  get selectedResponseType() {
    return this.form.controls.paymentResponseMessageTypeId.valid
      ? this.paymentResponseTypes.find(
          (type) =>
            type.paymentResponseMessageTypeId ===
            this.form.controls.paymentResponseMessageTypeId.value
        )
      : null;
  }
}
